import { useState, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { EmployeeRequestMeetingModal } from '../../../components/custom_components/EmployeeMeetingRequest/EmployeeRequestMeetingModal'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { useAuth } from '../../../context/auth-context'
import { employeeDisplayTinyInfoSorter } from '../../../shared/sortUtils'
import useAllReviewersOfEmployee from '../../../graphql/reviewerEmployee/hooks/useAllReviewersOfEmployee'
import PerButton from '../../../components/custom_components/Button'
import useMeetingRequestOptions from './useMeetingRequestOptions'
import { MeetingInviteOptionsExtended, MeetingType } from '../../../types'

interface NewMeetingInviteButtonProps {
  btnSize?: 'small' | 'large'
  fullWidth?: boolean
  pastel?: boolean
  context: 'OwnGrowth' | 'MyMeetings'
}

export const RequestMeetingInviteButton: FC<NewMeetingInviteButtonProps> = ({ btnSize, fullWidth = false, pastel = false, context }) => {
  const { user } = useAuth()
  const [t] = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data, error, loading } = useAllReviewersOfEmployee(user.employeeId)
  const { data: meetingOptionsData, error: meetingOptionsError, loading: meetingOptionsLoading } = useMeetingRequestOptions(user.employeeId)

  const isInitialLoad =
    !data ||
    !data.allReviewersByEmployeeId ||
    !meetingOptionsData ||
    error ||
    meetingOptionsError ||
    loading ||
    (meetingOptionsLoading && !isModalOpen)
  if (isInitialLoad) {
    return (
      <LoadingAndErrors
        hasNoData={!data || !meetingOptionsData}
        error={error || meetingOptionsError}
        isLoading={loading || meetingOptionsLoading}
        showSpinner
      />
    )
  }

  const cleanAndCloseModal = () => {
    setIsModalOpen(false)
  }

  const sortedReviewers = [...data.allReviewersByEmployeeId].sort((e1, e2) =>
    employeeDisplayTinyInfoSorter(user.nameDisplayPreference, e1, e2),
  )
  const meetingOptions: MeetingInviteOptionsExtended = meetingOptionsData.meetingRequestOptionsForEmployee
  const appraisalOption: MeetingType | undefined = meetingOptions.possible.find((option) =>
    [MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(option),
  )

  const areThereMeetingOptionsAvailable = () => {
    if (meetingOptions.possible.length === 0) {
      return false
    }

    if (context === 'OwnGrowth' && !!appraisalOption && meetingOptions.isAppraisalEnabled && !meetingOptions.tagAssignmentIsInProgress) {
      return true
    } else if (
      context === 'MyMeetings' &&
      (meetingOptions.possible.length > 1 ||
        ![MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(meetingOptions.possible[0]) ||
        (meetingOptions.isAppraisalEnabled && !meetingOptions.tagAssignmentIsInProgress))
    ) {
      return true
    }

    return false
  }

  const disabledButton = sortedReviewers.length === 0 || !areThereMeetingOptionsAvailable()

  const renderTooltipWithButtonDisabled = () => {
    return sortedReviewers.length === 0
      ? t(`profile.meetings.request.missingReviewerTooltip`)
      : context === 'OwnGrowth'
      ? appraisalOption
        ? t(`profile.meetings.request.appraisalNotAvailableTooltip`, { appraisalType: t(`meetingTypes.${appraisalOption}`) })
        : t(`profile.meetings.request.appraisalAlreadyRequestedTooltip`)
      : t(`profile.meetings.request.missingMeetingOptionTooltip`)
  }

  return (
    <>
      {isModalOpen && (
        <EmployeeRequestMeetingModal
          reviewerList={sortedReviewers}
          closeModal={cleanAndCloseModal}
          visible={isModalOpen}
          meetingOptions={meetingOptions!}
          context={context}
        />
      )}

      <PerButton
        data-e2e-empty-meetings-table-invite-btn
        onClick={(e) => {
          e.preventDefault() // We should refactor button to not emit native event!
          setIsModalOpen(true)
        }}
        before="chat_fill"
        color="primary"
        size={btnSize && 'small'}
        width={fullWidth ? 'fullWidth' : undefined}
        variant={pastel ? 'pastel' : undefined}
        text={
          context === 'OwnGrowth'
            ? t(`employeeInvite.headlineAppraisal`, { appraisalType: t(`meetingTypes.${appraisalOption || MeetingType.Appraisal}`) })
            : t(`employeeInvite.headline`)
        }
        disabled={disabledButton}
        tooltip={disabledButton && renderTooltipWithButtonDisabled()}
      />
    </>
  )
}
